import { useEffect, useState } from 'react';
import {
  X,
  Phone,
  Mail,
  Calendar,
  User,
  AlertTriangle,
  MapPin,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import dayjs from 'dayjs';
import { formatCurrency } from '@/utils/currency';
import { buildWhatsappUrl } from '@/utils/whatsapp';
import { getInitials, formatAmountWithBs } from '@/utils/crmHelpers';
import { formatDateDDMMYY, getDaysAbsent, getDaysUntil } from '@/utils/date';
import { extractRelationalSaleItemEntities } from '@/utils/relationalResolution';
import { resolveLocationName } from '@/utils/locationResolution';
import { useAuthStore, useSettingsStore, useCrmStore } from '@/stores';
import { saveCrmClients, saveCustomerStatsBatch } from '@/lib/db';
import * as api from '@/lib/api';
import type { CrmClient } from '@/types/crm';

export interface ClientDetailModalProps {
  client: CrmClient | null;
  isOpen: boolean;
  onClose: () => void;
}

interface TimelineItem {
  id: string;
  type: 'appointment' | 'sale';
  date: string;
  time: string;
  rawDate: string;
  service: string;
  staff: string;
  locationName: string;
  price: number;
  status: string;
  isCancelled: boolean;
  isSale: boolean;
}

function buildTimeline(
  appts: any[],
  sales: any[],
  included: any[],
  locations: any[] = [],
  locationAliases: Record<string, string> = {},
  locationNameMode: any = 'alias'
): TimelineItem[] {
  const items: TimelineItem[] = [];

  // 1. Map appointments
  for (let i = 0; i < appts.length; i++) {
    const appt = appts[i];
    const b = appt.attributes?.bookings?.[0] || appt.attributes || {};
    const date =
      b['start-wall']?.date ||
      appt.attributes?.['start-wall']?.date ||
      b.date ||
      '';
    const time =
      b['start-wall']?.time ||
      appt.attributes?.['start-wall']?.time ||
      b.time ||
      '';
    const rawDate =
      b['start-wall']?.['date-time'] ||
      appt.attributes?.['start-wall']?.['date-time'] ||
      (date ? `${date}${time ? 'T' + time : ''}` : '');
    const service =
      b.service?.name ||
      b['service-name'] ||
      appt.attributes?.service?.name ||
      'Servicio';
    const staff =
      b.employee?.name ||
      b['employee-name'] ||
      appt.attributes?.employee?.name ||
      '';
    const rawPrice = b.price ?? b['base-price'] ?? b['retail-price'] ?? 0;
    const price = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice) || 0;
    const locId = String(b.location?.id || appt.attributes?.location?.id || b['location-id'] || appt.attributes?.['location-id'] || '');
    const rawLocName = b.location?.name || appt.attributes?.location?.name || b['location-name'] || appt.attributes?.['location-name'] || '';
    const locationName = resolveLocationName(
      locId,
      rawLocName,
      locations,
      locationAliases,
      locationNameMode
    );
    const status = b.status || appt.attributes?.status || '';
    const isCancelled = status === 'cancelled' || status === 'no-show';

    items.push({
      id: appt.id || `appt-${i}`,
      type: 'appointment',
      date,
      time,
      rawDate: rawDate || date,
      service,
      staff,
      locationName,
      price,
      status,
      isCancelled,
      isSale: false,
    });
  }

  // 2. Map sales fallback / additions
  for (let j = 0; j < sales.length; j++) {
    const sale = sales[j];
    const saleAttrs = sale?.attributes || {};
    const rawSaleDate =
      saleAttrs['invoice-date'] ||
      saleAttrs['created-at'] ||
      saleAttrs['issued-at'] ||
      saleAttrs.date ||
      '';

    let date = rawSaleDate;
    let time = '';
    if (rawSaleDate.includes('T')) {
      const [d, t] = rawSaleDate.split('T');
      date = d;
      time = t ? t.substring(0, 5) : '';
    }

    const saleItemsRel = sale?.relationships?.['sale-items']?.data || [];
    const relIds = Array.isArray(saleItemsRel)
      ? saleItemsRel.map((r: any) => String(r.id))
      : [];

    let matchedItems = included.filter((inc: any) =>
      relIds.includes(String(inc.id))
    );

    if (matchedItems.length === 0 && included.length > 0) {
      matchedItems = included.filter(
        (inc: any) =>
          inc.type === 'sale-items' ||
          inc.type === 'services' ||
          inc.type === 'line-items' ||
          inc.type === 'service-sale-items'
      );
    }

    const serviceNameFromItems = matchedItems
      .map(
        (inc: any) =>
          inc.attributes?.name ||
          inc.attributes?.['item-name'] ||
          inc.attributes?.['service-name']
      )
      .filter(Boolean)
      .join(', ');

    const saleEntities = extractRelationalSaleItemEntities(sale, included);
    const service =
      saleEntities.service ||
      serviceNameFromItems ||
      saleAttrs['service-name'] ||
      saleAttrs['item-name'] ||
      saleAttrs.description ||
      'Venta de servicio';

    const staff = saleEntities.staff || '';

    const saleLocId = String(saleAttrs['location-id'] || saleAttrs.location?.id || '');
    const rawSaleLocName = saleAttrs['location-name'] || saleAttrs.location?.name || '';
    const locationName = resolveLocationName(
      saleLocId,
      rawSaleLocName,
      locations,
      locationAliases,
      locationNameMode
    );

    const rawPrice =
      saleAttrs['total-gross-value'] ??
      saleAttrs['total-amount'] ??
      saleAttrs['grand-total'] ??
      saleAttrs['total-net-value'] ??
      saleAttrs.total ??
      saleAttrs.price ??
      (matchedItems[0]?.attributes?.['listing-total-price'] ?? 0);
    const price = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice) || 0;

    const status = saleAttrs.status || 'completed';
    const isCancelled = status === 'voided' || status === 'refunded';

    const isDuplicate = items.some(
      (appt) =>
        appt.type === 'appointment' &&
        (
          (appt.date && date && appt.date === date && (appt.service.toLowerCase() === service.toLowerCase() || service === 'Venta de servicio' || appt.service === 'Servicio')) ||
          (appt.id && (saleAttrs['appointment-id'] === appt.id || saleAttrs['booking-id'] === appt.id))
        )
    );

    if (!isDuplicate) {
      items.push({
        id: sale.id ? `sale-${sale.id}` : `sale-${j}`,
        type: 'sale',
        date,
        time,
        rawDate: rawSaleDate || date,
        service,
        staff,
        locationName,
        price,
        status,
        isCancelled,
        isSale: true,
      });
    }
  }

  // 3. Chronological sorting (newest first)
  items.sort((a, b) => {
    const timeA = a.rawDate ? new Date(a.rawDate).getTime() : 0;
    const timeB = b.rawDate ? new Date(b.rawDate).getTime() : 0;
    if (isNaN(timeA) || isNaN(timeB)) {
      return (b.date || '').localeCompare(a.date || '');
    }
    return timeB - timeA;
  });

  return items;
}

export function ClientDetailModal({ client, isOpen, onClose }: ClientDetailModalProps) {
  const { workspaceId, locationId, locations } = useAuthStore();
  const settings = useSettingsStore();
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [rawAppointments, setRawAppointments] = useState<any[]>([]);
  const [rawSales, setRawSales] = useState<any[]>([]);
  const [rawSalesIncluded, setRawSalesIncluded] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !client || !workspaceId) return;

    let isMounted = true;
    // oxlint-disable-next-line react/set-state-in-effect
    setLoading(true);
    setHasMore(true);

    Promise.all([
      api.getCustomerAppointments(client.customerId, workspaceId, locationId || '', 15).catch(() => null),
      api.getCustomerSales(client.customerId, workspaceId).catch(() => null),
      api.getCustomerStats(client.customerId, workspaceId).catch(() => null),
    ])
      .then(([apptsRes, salesRes, statsRes]) => {
        if (!isMounted) return;

        const apptsData = apptsRes?.data?.data || apptsRes?.data || [];
        const parsedAppts = Array.isArray(apptsData) ? apptsData : [];
        setRawAppointments(parsedAppts);

        const salesData = salesRes?.data?.data || salesRes?.data || [];
        const parsedSales = Array.isArray(salesData) ? salesData : [];
        const included = salesRes?.data?.included || (salesRes as any)?.included || [];
        const parsedIncluded = Array.isArray(included) ? included : [];
        setRawSales(parsedSales);
        setRawSalesIncluded(parsedIncluded);

        const merged = buildTimeline(
          parsedAppts,
          parsedSales,
          parsedIncluded,
          locations,
          settings.locationAliases,
          settings.locationNameMode
        );
        setTimeline(merged);

        if (parsedAppts.length < 15) {
          setHasMore(false);
        }
        setStats(statsRes?.data?.data?.attributes || statsRes?.data?.attributes || null);

        // === WRITE-BACK: Sync fresh data to CRM store and Dexie ===
        try {
          const cid = client.customerId || client.id;
          const now = dayjs();
          let nextApptDate = '';
          let nextApptStaff = '';
          let nextApptService = '';
          let nextApptLocation = '';
          let nextApptLocId = '';
          let lastApptDate = '';
          let lastApptStaff = '';
          let lastApptService = '';
          let lastApptLocation = '';
          let lastApptLocId = '';

          for (const appt of parsedAppts) {
            const attr = appt?.attributes || {};
            const b = attr.bookings?.[0] || {};
            const dateStr = b['start-wall']?.date || attr['start-wall']?.date || '';
            if (!dateStr) continue;

            const staff = b.employee?.name || attr.employee?.name || '';
            const service = b.service?.name || b['service-name'] || attr.service?.name || '';
            const locId = String(b.location?.id || attr.location?.id || b['location-id'] || attr['location-id'] || '');
            const rawLocName = b.location?.name || attr.location?.name || b['location-name'] || attr['location-name'] || '';
            const locName = resolveLocationName(
              locId,
              rawLocName,
              locations,
              settings.locationAliases,
              settings.locationNameMode
            );
            const apptDay = dayjs(dateStr).startOf('day');

            if (apptDay.isSameOrAfter(now.startOf('day'))) {
              if (!nextApptDate || apptDay.isBefore(dayjs(nextApptDate))) {
                nextApptDate = dateStr;
                nextApptStaff = staff;
                nextApptService = service;
                nextApptLocation = locName;
                nextApptLocId = locId;
              }
            } else {
              if (!lastApptDate || apptDay.isAfter(dayjs(lastApptDate))) {
                lastApptDate = dateStr;
                lastApptStaff = staff;
                lastApptService = service;
                lastApptLocation = locName;
                lastApptLocId = locId;
              }
            }
          }

          const statsAttrs = statsRes?.data?.data?.attributes || statsRes?.data?.attributes || {};
          const totalAppts = Number(statsAttrs['completed-bookings-count'] ?? statsAttrs.completedBookingsCount ?? 0);
          const outstandingSales = Number(statsAttrs['outstanding-sales'] ?? statsAttrs.outstandingSales ?? 0);
          const totalSalesVal = Number(statsAttrs['total-sales'] ?? statsAttrs.totalSales ?? 0);

          const clientUpdates: Record<string, any> = {};
          if (nextApptDate) {
            clientUpdates.nextAppointmentDate = nextApptDate;
            clientUpdates.nextStaffMember = nextApptStaff;
            clientUpdates.nextServiceName = nextApptService;
            clientUpdates.nextLocationName = nextApptLocation;
            clientUpdates.nextLocationId = nextApptLocId;
            clientUpdates.daysUntilNext = getDaysUntil(nextApptDate);
          }
          if (lastApptDate) {
            clientUpdates.lastAppointmentDate = lastApptDate;
            clientUpdates.lastStaffMember = lastApptStaff;
            clientUpdates.lastServiceName = lastApptService;
            clientUpdates.lastLocationName = lastApptLocation;
            clientUpdates.lastLocationId = lastApptLocId;
            clientUpdates.daysAbsent = getDaysAbsent(lastApptDate);
          }
          if (totalAppts > 0) clientUpdates.totalAppointments = totalAppts;
          if (!isNaN(outstandingSales)) clientUpdates.outstandingAmount = outstandingSales;
          if (!isNaN(totalSalesVal)) clientUpdates.totalSales = totalSalesVal;

          if (Object.keys(clientUpdates).length > 0) {
            useCrmStore.getState().updateClientsBatch({ [cid]: clientUpdates });
            saveCrmClients([{ ...client, ...clientUpdates }]).catch(console.warn);
          }

          // Persist to Dexie (statsCache + crmClients)
          const statsCacheItem = {
            customerId: cid,
            appointments: totalAppts || undefined,
            lastAppointment: lastApptDate || undefined,
            staff: lastApptStaff || undefined,
            serviceName: lastApptService || undefined,
            locationName: lastApptLocation || undefined,
            lastLocationId: lastApptLocId || undefined,
            nextAppointment: nextApptDate || undefined,
            nextStaff: nextApptStaff || undefined,
            nextServiceName: nextApptService || undefined,
            nextLocationName: nextApptLocation || undefined,
            nextLocationId: nextApptLocId || undefined,
            daysUntilNext: nextApptDate ? getDaysUntil(nextApptDate) : undefined,
            daysAbsent: lastApptDate ? getDaysAbsent(lastApptDate) : undefined,
            outstandingSales: !isNaN(outstandingSales) ? outstandingSales : undefined,
            totalSales: !isNaN(totalSalesVal) ? totalSalesVal : undefined,
            lastFetch: Date.now(),
          };
          saveCustomerStatsBatch([statsCacheItem]).catch(console.warn);

          useCrmStore.getState().setStatsCacheBatch({ [cid]: statsCacheItem });
        } catch (writeBackErr) {
          console.warn('Modal write-back failed (non-critical):', writeBackErr);
        }
      })
      .catch((err) => {
        if (isMounted) console.error('Error fetching customer details:', err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, client, workspaceId, locationId]);

  const handleLoadMore = async () => {
    if (!client || !workspaceId || loadingMore) return;
    const nextLimit = rawAppointments.length + 15;
    setLoadingMore(true);
    try {
      const res = await api.getCustomerAppointments(client.customerId, workspaceId, locationId || '', nextLimit);
      const apptsData = res?.data?.data || res?.data || [];
      const newAppts = Array.isArray(apptsData) ? apptsData : [];
      if (newAppts.length <= rawAppointments.length || newAppts.length < nextLimit) {
        setHasMore(false);
      }
      setRawAppointments(newAppts);
      setTimeline(buildTimeline(
        newAppts,
        rawSales,
        rawSalesIncluded,
        locations,
        settings.locationAliases,
        settings.locationNameMode
      ));
    } catch (err) {
      console.error('Error loading more appointments:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (!isOpen || !client) return null;

  const whatsappUrl = client.phone
    ? buildWhatsappUrl(client.phone, '', settings.whatsappMode)
    : '';

  const initials = getInitials(client.name);

  const totalCompletedBookings =
    stats?.['completed-bookings-count'] ??
    stats?.completedBookingsCount ??
    client.totalAppointments ??
    0;

  const totalSalesAmount =
    stats?.['total-sales-amount'] ??
    stats?.totalSalesAmount ??
    client.totalSales ??
    0;

  const totalCancelledBookings =
    stats?.['cancelled-bookings-count'] ??
    stats?.cancelledBookingsCount ??
    client.cancelledAppointments ??
    0;

  const activeRate = settings.manualRate || null;

  const totalSalesFormatted = formatAmountWithBs(
    totalSalesAmount,
    settings.currencyCode || 'EUR',
    activeRate
  );

  const debtFormatted = formatAmountWithBs(
    client.outstandingAmount || 0,
    settings.currencyCode || 'EUR',
    activeRate
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-detail-title"
    >
      <div className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-scale-in">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-surface-secondary/40">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full bg-brand-primary/10 text-brand-primary font-bold flex items-center justify-center text-sm shrink-0 border border-brand-primary/20">
              {initials}
            </div>
            <div className="min-w-0">
              <h3 id="client-detail-title" className="font-bold text-text-primary text-base truncate">
                {client.name}
              </h3>
              <p className="text-xs text-text-muted">ID: {client.customerId}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar modal"
            className="p-2 rounded-xl text-text-muted hover:text-text-primary hover:bg-surface-tertiary transition-colors cursor-pointer shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
          {/* Contact Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-surface-secondary rounded-xl border border-border">
            {client.phone ? (
              <div className="flex items-center gap-2 min-w-0">
                <Phone size={14} className="text-brand-primary shrink-0" />
                <a
                  href={`tel:${client.phone}`}
                  className="hover:underline text-text-primary font-medium truncate"
                  title="Llamar"
                >
                  {client.phone}
                </a>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-text-muted">
                <Phone size={14} className="shrink-0 opacity-40" />
                <span>Sin teléfono</span>
              </div>
            )}
            {client.email ? (
              <div className="flex items-center gap-2 min-w-0">
                <Mail size={14} className="text-brand-primary shrink-0" />
                <a
                  href={`mailto:${client.email}`}
                  className="hover:underline text-text-primary truncate"
                  title={client.email}
                >
                  {client.email}
                </a>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-text-muted">
                <Mail size={14} className="shrink-0 opacity-40" />
                <span>Sin correo</span>
              </div>
            )}
          </div>

          {/* Quick Contact Actions */}
          {client.phone && (
            <div className="flex gap-2">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2.5 px-3 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
              >
                <MessageSquare size={15} />
                <span>Abrir WhatsApp</span>
              </a>
              <a
                href={`tel:${client.phone}`}
                className="py-2.5 px-4 bg-surface-secondary hover:bg-surface-tertiary text-text-primary rounded-xl font-medium border border-border flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
              >
                <Phone size={14} />
                <span>Llamar</span>
              </a>
            </div>
          )}

          {/* Outstanding Debt Alert Banner (if applicable) */}
          {client.outstandingAmount > 0.01 && (
            <div className="flex items-center justify-between p-3 bg-brand-danger/10 border border-brand-danger/20 rounded-xl text-brand-danger">
              <span className="font-semibold flex items-center gap-1.5 text-xs">
                <AlertTriangle size={15} className="shrink-0" />
                Saldo Deudor Pendiente
              </span>
              <div className="text-right">
                <span className="font-bold text-sm block">
                  {debtFormatted.original}
                </span>
                {debtFormatted.bs && (
                  <span className="text-[10px] opacity-80 block">{debtFormatted.bs}</span>
                )}
              </div>
            </div>
          )}

          {/* Financial and Booking Statistics */}
          <div>
            <h4 className="font-bold text-text-primary mb-2 text-xs uppercase tracking-wider">
              Historial y Fidelidad
            </h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-3 bg-surface-secondary border border-border rounded-xl text-center">
                <span className="text-text-muted block text-[10px] uppercase tracking-wide">Citas Totales</span>
                <span className="text-base font-bold text-text-primary block mt-0.5">
                  {totalCompletedBookings}
                </span>
              </div>
              <div className="p-3 bg-surface-secondary border border-border rounded-xl text-center">
                <span className="text-text-muted block text-[10px] uppercase tracking-wide">Total Ventas</span>
                <span className="text-base font-bold text-text-primary block mt-0.5">
                  {totalSalesFormatted.original}
                </span>
                {totalSalesFormatted.bs && (
                  <span className="text-[10px] text-text-muted block mt-0.5">
                    {totalSalesFormatted.bs}
                  </span>
                )}
              </div>
              <div className="p-3 bg-surface-secondary border border-border rounded-xl text-center">
                <span className="text-text-muted block text-[10px] uppercase tracking-wide">Cancelaciones</span>
                <span className="text-base font-bold text-brand-danger block mt-0.5">
                  {totalCancelledBookings}
                </span>
              </div>
            </div>
          </div>

          {/* Past Appointments / Visits Timeline */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-text-primary text-xs uppercase tracking-wider">
                Historial de Citas y Visitas
              </h4>
              {timeline.length > 0 && (
                <span className="text-[11px] text-text-muted">
                  {totalCompletedBookings > timeline.length
                    ? `${timeline.length} de ${totalCompletedBookings} citas`
                    : `${timeline.length} cita${timeline.length !== 1 ? 's' : ''}`}
                </span>
              )}
            </div>

            {loading ? (
              <div className="py-8 flex flex-col items-center justify-center gap-2 text-text-muted bg-surface-secondary/50 rounded-xl border border-border">
                <Loader2 size={20} className="animate-spin text-brand-primary" />
                <p className="text-xs">Cargando historial de citas...</p>
              </div>
            ) : timeline.length === 0 ? (
              <div className="py-6 px-4 text-center bg-surface-secondary rounded-xl border border-border text-text-muted">
                <Calendar size={24} className="mx-auto mb-1.5 opacity-30" />
                <p>No hay citas ni visitas registradas</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-0.5">
                {timeline.map((item: TimelineItem, idx: number) => {
                  return (
                    <div
                      key={item.id || `timeline-${idx}`}
                      className="p-3 bg-surface-secondary/70 border border-border rounded-xl flex items-center justify-between gap-3 hover:bg-surface-secondary transition-colors"
                    >
                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-text-primary text-xs truncate">
                            {item.service}
                          </span>
                          {item.isSale && (
                            <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-medium bg-brand-primary/10 text-brand-primary border border-brand-primary/20 shrink-0">
                              Venta
                            </span>
                          )}
                          {item.isCancelled && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-bold bg-brand-danger/10 text-brand-danger border border-brand-danger/20 shrink-0">
                              {item.status === 'no-show' ? 'No asistió' : item.status === 'refunded' ? 'Reembolsada' : 'Cancelada'}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-text-muted">
                          {item.date && (
                            <span className="flex items-center gap-1">
                              <Calendar size={11} className="shrink-0" />
                              <span>{formatDateDDMMYY(item.date)} {item.time ? `• ${item.time}` : ''}</span>
                            </span>
                          )}
                          {item.staff && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <User size={11} className="shrink-0" />
                                <span className="truncate">{item.staff}</span>
                              </span>
                            </>
                          )}
                          {item.locationName && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <MapPin size={11} className="shrink-0" />
                                <span className="truncate">{item.locationName}</span>
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <span className="font-bold text-text-primary text-xs shrink-0">
                        {formatCurrency(item.price, settings.currencyCode || 'EUR')}
                      </span>
                    </div>
                  );
                })}

                {hasMore && rawAppointments.length >= 15 && (
                  <div className="pt-1.5 pb-0.5">
                    <button
                      type="button"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="w-full py-2 px-3 bg-surface-secondary hover:bg-surface-tertiary border border-border rounded-xl text-xs font-medium text-text-primary transition-colors flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={13} className="animate-spin text-brand-primary" />
                          <span>Cargando más citas...</span>
                        </>
                      ) : (
                        <span>Cargar más citas</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-3 border-t border-border bg-surface-secondary/30 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="py-2 px-5 bg-surface-secondary hover:bg-surface-tertiary text-text-primary border border-border rounded-xl font-medium cursor-pointer transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
